import React from 'react';
import { DragDropContext, Draggable, Droppable, type DropResult } from 'react-beautiful-dnd';
import { Input, Icon } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';

import type { CustomFieldOptionRow } from '../../../../feature/components/CustomFieldsSection/customField.types';

import styles from './CustomFieldModal.scss';

export type CustomFieldModalOptionRowsEditorProps = {
  optionRows: CustomFieldOptionRow[];
  onUpdate: (id: string, update: Partial<Pick<CustomFieldOptionRow, 'displayText' | 'value'>>) => void;
  onRemove: (id: string) => void;
  onDragEnd: (result: DropResult) => void;
  optionsError?: string;
  t: (key: string) => string;
};

export function CustomFieldModalOptionRowsEditor({
  optionRows,
  onUpdate,
  onRemove,
  onDragEnd,
  optionsError,
  t,
}: CustomFieldModalOptionRowsEditorProps): React.ReactElement {
  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="custom-field-options">
          {provided => (
            <ul
              className={styles.options}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {optionRows.map((row, index) => (
                <Draggable key={row.id} draggableId={row.id} index={index}>
                  {(dragProvided, snapshot) => (
                    <li
                      ref={dragProvided.innerRef}
                      className={styles.optionRow}
                      data-is-dragging={snapshot.isDragging}
                      {...dragProvided.draggableProps}
                    >
                      <span
                        title={t('web.admin.visitorManagement.customField.form.options.dragReorderTooltip')}
                        className={styles.iconReorder}
                        {...dragProvided.dragHandleProps}
                      >
                        <Icon name="bars" set={ICON_SET_FONTAWESOME} />
                      </span>
                      <div className={styles.optionInputGroup}>
                        <Input
                          className={styles.optionInput}
                          label="web.admin.content.friendlyProperty.labels.optionLabel"
                          value={row.displayText}
                          onChange={(val) => onUpdate(row.id, { displayText: val })}
                          placeholder={t('web.admin.content.friendlyProperty.placeholder.optionLabel')}
                          fieldName={`${row.id}-displayText`}
                          ariaLabel={t('web.admin.content.friendlyProperty.labels.optionLabel')}
                        />
                      </div>
                      <div className={styles.optionInputGroup}>
                        <Input
                          className={styles.optionInput}
                          label="web.admin.content.friendlyProperty.labels.optionValue"
                          value={row.value}
                          onChange={(val) => onUpdate(row.id, { value: val })}
                          placeholder={t('web.admin.content.friendlyProperty.placeholder.optionValue')}
                          fieldName={`${row.id}-value`}
                          ariaLabel={t('web.admin.content.friendlyProperty.labels.optionValue')}
                        />
                      </div>
                      <button
                        type="button"
                        className={styles.removeIconButton}
                        onClick={() => onRemove(row.id)}
                        aria-label={t('web.admin.visitorManagement.customField.form.options.removeTooltip')}
                      >
                        <Icon
                          className={styles.removeIcon}
                          name="times-circle"
                          set={ICON_SET_FONTAWESOME}
                        />
                      </button>
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
      {optionsError && <span className={styles.fieldError}>{optionsError}</span>}
    </>
  );
}
